import { api, API_BASE } from '../../services/api';

// AI-GEOLOGIST-V1 — POST /api/v1/agents/geologist/query
// Evidence-grounded geological analysis. All numbers are assembled
// deterministically by the backend; the LLM writes prose only.

export type ConfLevel = 'high' | 'medium' | 'low';
export type FactStatus = 'verified' | 'sourced' | 'extracted' | 'pending_review';
export type ProvenanceCompleteness = 'full' | 'partial' | 'source_only' | 'none';
export type Comparability =
  | 'directly_comparable' | 'comparable_with_caution'
  | 'not_directly_comparable' | 'insufficient_metadata';
export type GapType =
  | 'missing_in_knowledge_set' | 'source_registered_not_parsed'
  | 'present_provenance_incomplete' | 'not_required_for_intent';
export type QueryIntent =
  | 'composition_query' | 'reserve_query' | 'spatial_query'
  | 'modification_feasibility_query' | 'historical_comparison_query'
  | 'out_of_scope' | 'general';
export type AnswerStatus =
  | 'answered' | 'insufficient_evidence' | 'not_in_knowledge_set'
  | 'source_registered_not_parsed' | 'out_of_agent_scope' | 'clarification_required'
  /** deposit known, but this deployment's graph has no node for it — route to the preview tier */
  | 'graph_unpublished';
export type Priority = 'critical' | 'high' | 'medium' | 'low';
export type ProvenanceTier =
  | 'origin_unknown' | 'attributed_unverified' | 'primary_source_found'
  | 'page_verified' | 'modern_confirmed';

export interface ResolvedScope {
  deposit: string | null;
  deposit_code: string | null;
  material_code: string | null;
  material_name: string | null;
  indicators: string[];
  years: number[];
  sections: string[];
  candidate_deposits: string[];
}

export interface EvidenceFact {
  fact_id: string;
  parameter: string;
  value: string | null;
  value_text: string | null;
  unit: string | null;
  year: number | null;
  sample: string | null;
  section: string | null;
  method: string | null;
  source_code: string | null;
  source_title: string | null;
  page: number | null;
  table: string | null;
  evidence_status: string | null;
  review_status: string | null;
  status: FactStatus;
  provenance_completeness: ProvenanceCompleteness;
  provenance_tier: ProvenanceTier;
}

export interface ReviewRequiredItem {
  parameter: string;
  value: string | null;
  unit: string | null;
  year: number | null;
  attributed_source: string | null;
  provenance_tier: ProvenanceTier;
  limitation: string;
  recommended_verification_action: string;
}

export interface ConflictValue {
  value: string | null;
  unit: string | null;
  year: number | null;
  sample: string | null;
  source_code: string | null;
}

export interface Conflict {
  parameter: string;
  values: ConflictValue[];
  reason: string;
  comparability: Comparability;
  missing_metadata: string[];
}

export interface DataGap {
  missing_item: string;
  why_it_matters: string;
  priority: Priority;
  gap_type: GapType;
}

export interface NextStep {
  action: string;
  purpose: string;
  priority: Priority;
}

export interface SourceRef {
  source_code: string;
  source_title: string | null;
  source_kind: string | null;
  year: number | null;
  via: string[];
  content_parsed: boolean;
  chunk_count: number;
  inclusion_reason: string;
}

export interface ConfidenceBreakdown {
  evidence_confidence: ConfLevel;
  cross_source_consistency: ConfLevel;
  conclusion_confidence: ConfLevel;
}

export interface ProvenanceSummary {
  full: number;
  partial: number;
  source_only: number;
  none: number;
}

export interface GeologistTrace {
  resolved_nodes: string[];
  properties_read: number;
  claims_read: number;
  sources_read: number;
  chunks_read: number;
  citation_coverage: number;
  provenance: ProvenanceSummary;
}

export interface GeologistResponse {
  query: string;
  query_intent: QueryIntent;
  resolved_scope: ResolvedScope;
  answer_status: AnswerStatus;
  limitation_reason: string | null;
  direct_answer: string;
  confidence: ConfidenceBreakdown;
  evidence_facts: EvidenceFact[];
  conflicts: Conflict[];
  interpretation: string;
  data_gaps: DataGap[];
  recommended_next_steps: NextStep[];
  review_required_evidence: ReviewRequiredItem[];
  sources: SourceRef[];
  trace: GeologistTrace;
  presentation_markdown: string | null;
}

export interface GeologistQueryResult {
  execution_log_id: string;
  response: GeologistResponse;
}

export interface GeologistFilters {
  years?: number[];
  source_codes?: string[];
  indicators?: string[];
}

export const DEMO_SCENARIOS: { label: string; question: string }[] = [
  { label: 'Подтверждённые свойства сырья',
    question: 'Какие подтверждённые данные есть по составу и свойствам Саригюхского бентонита? Приведи значения и источники.' },
  { label: 'Сравнить отчёты 1963 / 1982 / 1989 / 2016',
    question: 'Сравни данные отчётов 1963, 1982, 1989 годов и исследования 2016 года. Где данные согласуются, а где расходятся?' },
  { label: 'CEC и обменный комплекс',
    question: 'Какие данные есть по CEC и обменным катионам Na, Ca, Mg и K?' },
  { label: 'Содержание монтмориллонита',
    question: 'Какие подтверждённые данные есть по содержанию монтмориллонита?' },
  { label: 'Расхождения между источниками',
    question: 'Где значения показателей расходятся между отчётами и почему их нельзя напрямую сравнивать?' },
  { label: 'Недостающие исследования',
    question: 'Какие параметры отсутствуют для объективной оценки состава сырья?' },
  { label: 'Пригодность к натриевой активации',
    question: 'Можно ли на основании существующих данных заключить, что сырьё пригодно для натриевой активации?' },
];

export const geologistApi = {
  query(question: string, filters?: GeologistFilters): Promise<GeologistQueryResult> {
    return api.request<GeologistQueryResult>('/agents/geologist/query', {
      method: 'POST',
      body: JSON.stringify({ question, filters: filters ?? {} }),
    });
  },
};

// ── P7.2: geological source upload + preview analysis ──────────────────────────
export interface GeoSourceCard {
  id: string;
  source_code: string | null;
  original_filename: string;
  report_year: number | null;
  source_title: string | null;
  size_bytes: number;
  sha256_short: string | null;
  detected_sheets: string[];
  extracted_record_count: number;
  conflicts: number;
  missing_values: number;
  borehole_count: number;
  coordinate_count: number;
  coordinate_status: string;
  pdf_evidence_status: string;
  canonical_core_missing: string[];
  validated_at: string | null;
  is_active: boolean;
  ok: boolean;
  statuses: string[];
}

export interface MetricCard { label: string; value: string; unit?: string | null; hint?: string | null; }
export interface TableBlock { title: string; columns: string[]; rows: (string | number | null)[][]; note?: string | null; total_rows?: number | null; truncated?: boolean; }
export interface ClaimView { text: string; evidence_ids: string[]; confidence: string; }
export interface BoreholeDetailCard {
  borehole_code: string; borehole_code_raw?: string | null;
  location?: string | null; exploration_context?: string | null;
  original_x_text?: string | null; original_y_text?: string | null; original_h_text?: string | null;
  parsed_x?: number | null; parsed_y?: number | null;
  source_page?: string | null; source_table?: string | null; source_code?: string | null;
  verification_status_ru?: string | null; review_required: boolean; warnings: string[];
}
export interface CalculationBreakdown { title: string; expression: string; inputs: Record<string, unknown>[]; result?: string | null; unit?: string | null; note?: string | null; }
export interface ConflictAlert { title: string; items: { object: string; printed?: string; calculated?: string; kind_ru?: string; note?: string }[]; }
export interface MissingEvidenceWarning { title: string; items: string[]; }
export interface SourceReference { source_id?: string | null; label: string; report_year?: number | null; filename?: string | null; }
export interface ChartBlock { kind: 'bar' | 'line' | 'scatter'; title: string; x_label?: string | null; y_label?: string | null; series: Record<string, unknown>[]; }
export interface ChartSeries { name: string; color: string; values: (number | null)[]; }
export interface DataChartBlock {
  kind: 'bar' | 'grouped_bar' | 'stacked_bar' | 'donut' | 'line';
  title: string; categories: string[]; series: ChartSeries[];
  unit?: string | null; note?: string | null;
}
export interface GeoSectionLayer {
  label: string; elevation_top: number; elevation_bottom: number;
  reserve_total_t?: number | null; dominant_mark?: string | null;
  dominant_color: string; marks: ChartSeries[];
}
export interface GeoSectionBlock {
  title: string; height_system: string; unit: string;
  elevation_max: number; elevation_min: number; max_reserve_t: number;
  layers: GeoSectionLayer[]; note?: string | null; disclaimer?: string | null;
}
export interface MapPoint {
  borehole_code: string; record_key?: string | null; report_year?: number | null;
  x_original?: number | null; y_original?: number | null; z_original?: number | null;
  original_x_text?: string | null; original_y_text?: string | null;
  source_crs?: string | null; longitude?: number | null; latitude?: number | null;
  source_page?: string | null; source_table?: string | null;
  linked_samples: string[]; depth_intervals: Record<string, unknown>[];
  properties: Record<string, unknown>[]; verification_status: string;
}
export type MapBlockStatus = 'ready' | 'no_coordinates' | 'unknown_crs_blocked' | 'transformation_error';
export interface MapBlock {
  status: MapBlockStatus; points: MapPoint[];
  source_crs_label?: string | null; residual_error_m?: number | null;
  library?: string | null; blocked_reason_user_text?: string | null;
  provisional?: boolean; provisional_note?: string | null;
}
export interface ReportGroupView { report_key: string; report_title?: string | null; report_year?: number | null; book_source_codes: string[]; }
export interface ReportBoreholeSummary { report_key: string; report_title?: string | null; books: string[]; lines: string[]; coordinate_availability?: string | null; map_verdict_ru?: string | null; }
export interface LocalPlanPoint {
  borehole_code: string; borehole_code_raw?: string | null;
  x: number; y: number; z?: number | null;
  location?: string | null; exploration_context?: string | null;
  original_x_text?: string | null; original_y_text?: string | null; original_h_text?: string | null;
  source_page?: string | null; source_table?: string | null; evidence_asset?: string | null;
  review_required: boolean; anomalous?: boolean; verification_status_ru?: string | null; evidence_id?: string | null;
}
export interface LocalSpatialPlan {
  title: string; banner: string;
  source_crs_text?: string | null; height_system_text?: string | null;
  points: LocalPlanPoint[]; locations: string[]; review_required_count: number;
}
export interface GlobalMapState { status: 'blocked' | 'enabled'; user_text: string; disabled_action?: string | null; }
export interface DebugPanel { intent: string; active_source_ids: string[]; raw_fact_count: number; conflict_count: number; coordinate_rows_detected: number; internal_notes: string[]; }

export interface MineralEntry {
  name_ru: string; role: 'main' | 'impurity' | 'accessory' | 'rock';
  color: string; icon: string; formula?: string | null; note?: string | null; evidence_id?: string | null;
}
export interface MineralBar { label: string; value: number; color: string; }
export interface MineralogyBlock {
  title: string; material_ru?: string | null; deposit_ru?: string | null;
  minerals: MineralEntry[]; host_rocks?: string | null; rock_types?: string | null;
  chemistry_sample?: string | null; chemistry_bars: MineralBar[];
  evidence_refs: string[]; disclaimer?: string | null;
}

export interface PreviewAnswer {
  language?: 'ru' | 'hy' | 'en';
  conversation_id?: string;
  knowledge_scope?: 'reviewed_draft';
  answer_summary: string;
  /** P7.5: the AI geologist's own specialist knowledge — labeled apart from file data. */
  expert_commentary?: string | null;
  intent: string;
  operation?: string | null;
  clarification_question?: string | null;
  mineralogy?: MineralogyBlock | null;
  active_source_ids: string[];
  report_groups: ReportGroupView[];
  claims: ClaimView[];
  metric_cards: MetricCard[];
  borehole_detail?: BoreholeDetailCard | null;
  report_borehole_summaries: ReportBoreholeSummary[];
  local_spatial_plan?: LocalSpatialPlan | null;
  global_map_state?: GlobalMapState | null;
  evidence_table?: TableBlock | null;
  comparison_table?: TableBlock | null;
  calculation_breakdown: CalculationBreakdown[];
  chart?: ChartBlock | null;
  charts?: DataChartBlock[] | null;
  geo_section?: GeoSectionBlock | null;
  map?: MapBlock | null;
  conflict_alert?: ConflictAlert | null;
  missing_evidence_warning?: MissingEvidenceWarning | null;
  coverage_notes: string[];
  source_reference: SourceReference[];
  debug?: DebugPanel | null;
}

export type GeoAgentMode = 'standard' | 'deep';
export type GeoAgentActivityStatus = 'active' | 'done' | 'warning';

export interface GeoAgentActivity {
  id: string;
  label: string;
  status: GeoAgentActivityStatus;
}

export interface GeoAgentDocumentSource {
  filename: string;
  file_id?: string | null;
  id?: string | null;
  source_group?: string | null;
  page?: number | null;
  version?: string | null;
  publication_state?: string | null;
  score?: number | null;
  relative_path?: string | null;
  artifact_role?: string | null;
  citation_pages?: number[];
}

export interface GeoArchiveCoverageSummary {
  controlled_source_groups: number;
  included_source_groups: number;
  registered_unique_files: number;
  registered_pages: number;
  searchable_chunks: number;
  embedded_chunks: number;
  semantic_model: string;
  scope_note: string;
}

export interface GeoAgentGrounding {
  knowledge_scope: 'published' | 'include_provisional';
  requested_knowledge_scope?: 'published' | 'include_provisional';
  source_groups: string[];
  requested_source_groups?: string[];
  scope_auto_expanded?: boolean;
  archive_wide_borehole_census?: boolean;
  chunk_count: number;
  assertion_count: number;
  conflict_count: number;
  gap_count: number;
  sources: GeoAgentDocumentSource[];
  conflicts: GeologyConflict[];
  evidence_gaps: GeologyGap[];
  corpus_fingerprint: string;
  evidence_fingerprint: string;
  archive_coverage?: { summary: GeoArchiveCoverageSummary };
  coordinate_catalogue_1975?: {
    included: boolean;
    summary?: {
      catalogue_records: number;
      borehole_records: number;
      test_pit_records: number;
      unique_borehole_identifiers: number;
      unique_test_pit_identifiers: number;
      overlapping_borehole_and_pit_identifiers: number;
      records_with_local_coordinates: number;
      unresolved_coordinate_records: number;
      candidate_wgs84_records: number;
      handwritten_height_corrections: number;
    } | null;
    matching_record_count: number;
  } | null;
  retrieval_diagnostics?: {
    retrieval_version?: string;
    strategy: string;
    semantic_used: boolean;
    semantic_model?: string | null;
    embedded_chunks_available: number;
    explicit_source_groups_in_question: string[];
    routed_source_groups?: string[];
    source_routing_reason?: string;
    source_scope_routed?: boolean;
    returned_source_groups: string[];
  };
}

export interface GeoAgentTokenUsage {
  input_tokens?: number;
  output_tokens?: number;
  total_tokens?: number;
  cached_input_tokens?: number;
  cache_write_tokens?: number;
}

export type GeoAgentModelId = 'gpt-5.6-sol' | 'gpt-5.6-terra' | 'gpt-5.6-luna';

export interface GeoAgentModelOption {
  id: GeoAgentModelId;
  name: string;
  role: 'quality' | 'balanced' | 'fast';
  supports_pro_mode: boolean;
  recommended: boolean;
}

export interface GeoAgentModelCatalog {
  default_model: GeoAgentModelId;
  items: GeoAgentModelOption[];
}

export type GeoAgentStreamEvent =
  | ({ type: 'activity' } & GeoAgentActivity)
  | { type: 'evidence'; answer: PreviewAnswer }
  | { type: 'grounding'; packet: GeoAgentGrounding }
  | { type: 'delta'; text: string }
  | { type: 'sources'; items: GeoAgentDocumentSource[] }
  | {
    type: 'done'; response_id: string; conversation_id: string;
    model: string; mode: GeoAgentMode; document_search: boolean;
    corpus_fingerprint?: string | null;
    cache_hit: boolean; cache_age_seconds?: number; cache_hits?: number;
    tokens_avoided?: number; token_usage?: GeoAgentTokenUsage;
  }
  | {
    type: 'error'; message: string;
    code?: 'ai_provider_quota_exhausted' | 'ai_provider_unavailable' | string;
    retryable?: boolean;
  };

export interface GeoAgentChatRequest {
  question: string;
  activeSourceIds?: string[];
  sourceGroups?: string[];
  conversationId: string;
  language: 'ru' | 'hy' | 'en';
  mode: GeoAgentMode;
  model: GeoAgentModelId;
  knowledgeScope?: 'published' | 'include_provisional';
}

export const geoSourcesApi = {
  list(): Promise<{ sources: GeoSourceCard[] }> {
    return api.request('/agents/geologist/sources');
  },
  setActive(id: string, active: boolean): Promise<GeoSourceCard> {
    return api.request(`/agents/geologist/sources/${id}/active`, {
      method: 'POST', body: JSON.stringify({ active }),
    });
  },
  remove(id: string): Promise<void> {
    return api.request(`/agents/geologist/sources/${id}`, { method: 'DELETE' });
  },
  previewQuery(
    question: string,
    options?: string[] | {
      activeSourceIds?: string[];
      language?: 'ru' | 'hy' | 'en';
      conversationId?: string;
    },
  ): Promise<PreviewAnswer> {
    const normalized = Array.isArray(options) ? { activeSourceIds: options } : (options ?? {});
    return api.request('/agents/geologist/preview-query', {
      method: 'POST',
      body: JSON.stringify({
        question,
        active_source_ids: normalized.activeSourceIds ?? null,
        language: normalized.language ?? null,
        conversation_id: normalized.conversationId ?? null,
        knowledge_scope: 'reviewed_draft',
      }),
    });
  },
  async streamAgentChat(
    request: GeoAgentChatRequest,
    onEvent: (event: GeoAgentStreamEvent) => void,
    signal?: AbortSignal,
  ): Promise<void> {
    const token = localStorage.getItem('bentonit_token');
    const response = await fetch(`${API_BASE}/api/v1/geology/ask/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        question: request.question,
        source_groups: request.sourceGroups ?? [],
        conversation_id: request.conversationId,
        language: request.language,
        mode: request.mode,
        model: request.model,
        knowledge_scope: request.knowledgeScope ?? 'include_provisional',
      }),
      signal,
    });
    if (response.status === 401) {
      localStorage.removeItem('bentonit_token');
      window.location.href = '/login';
      throw new Error('Unauthorized - Please login again');
    }
    if (!response.ok) {
      let message = `Agent request failed (${response.status})`;
      try {
        const payload = await response.json() as { detail?: string | { message?: string } };
        message = typeof payload.detail === 'string'
          ? payload.detail
          : payload.detail?.message || message;
      } catch { /* keep the HTTP message */ }
      throw new Error(message);
    }
    if (!response.body) throw new Error('The server returned an empty agent stream.');

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let pending = '';
    const consumeLine = (line: string) => {
      const normalized = line.trim();
      if (!normalized) return;
      const event = JSON.parse(normalized) as GeoAgentStreamEvent;
      onEvent(event);
      if (event.type === 'error') throw new Error(event.message);
    };

    while (true) {
      const { value, done } = await reader.read();
      pending += decoder.decode(value, { stream: !done });
      const lines = pending.split('\n');
      pending = lines.pop() ?? '';
      for (const line of lines) consumeLine(line);
      if (done) break;
    }
    if (pending.trim()) consumeLine(pending);
  },
  // XHR upload so we can report real byte-level progress (fetch cannot).
  upload(file: File, onProgress?: (pct: number) => void): Promise<GeoSourceCard> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const form = new FormData();
      form.append('file', file, file.name);
      xhr.open('POST', `${API_BASE}/api/v1/agents/geologist/sources`);
      const token = localStorage.getItem('bentonit_token');
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(JSON.parse(xhr.responseText) as GeoSourceCard);
        } else {
          let detail: unknown = null;
          try { detail = JSON.parse(xhr.responseText).detail; } catch { /* ignore */ }
          const msg = typeof detail === 'string'
            ? detail
            : ((detail as { message?: string })?.message || `Ошибка загрузки (${xhr.status})`);
          const err = new Error(msg) as Error & { status?: number; originalDetail?: unknown };
          err.status = xhr.status; err.originalDetail = detail;
          reject(err);
        }
      };
      xhr.onerror = () => reject(new Error('Не удалось связаться с сервером'));
      xhr.send(form);
    });
  },
};

// ── Geology V2: controlled archive and published evidence surface ───────────
export type GeologyPublicationState =
  | 'draft' | 'provisional_only' | 'in_review' | 'published' | 'retracted' | 'superseded';

export interface GeologyArchiveManifest {
  source_groups: number; packages: number; unique_files: number; exact_aliases: number;
  unique_bytes: number; registered_pages: number; searchable_chunks: number;
  open_gaps: number; open_conflicts: number; last_imported_at?: string | null;
  corpus_hash?: string | null;
}
export interface GeologySourceGroup {
  id: string; group_key: string; archival_year?: number | null;
  title_ru: string; title_en: string; default_publication_state: GeologyPublicationState;
  known_limitations: string[]; source_files: number; pages: number;
  open_gaps: number; open_conflicts: number;
}
export interface GeologyDataset {
  id: string; dataset_key: string; title_ru: string; title_en: string; commodity: string;
  description: string; group_key: string; active_version_id?: string | null;
  active_version?: string | null; active_publication_state?: GeologyPublicationState | null;
  version_count: number;
}
export interface GeologySourceFile {
  id: string; canonical_filename: string; relative_path: string; sha256: string;
  mime_type: string; size_bytes: number; integrity_status: string; page_count?: number | null;
  width_px?: number | null; height_px?: number | null; dpi?: number | null;
  is_original: boolean; metadata: Record<string, unknown>; group_key?: string | null; alias_count: number;
}
export interface GeologyCollar {
  id: string; borehole_key: string; printed_identifier: string;
  raw_x?: string | null; raw_y?: string | null; elevation_m?: number | null;
  source_crs?: string | null; coordinate_operation?: string | null;
  operation_accuracy_m?: number | null; practical_uncertainty_m?: number | null;
  verification_status: string; coordinate_role: string; metadata: Record<string, unknown>;
  latitude: number; longitude: number; group_key: string; version: string;
  publication_state: GeologyPublicationState;
  projected_x?: number | string | null; projected_y?: number | string | null;
}
export interface GeologyMapFeatures {
  type: 'FeatureCollection'; knowledge_scope: 'published' | 'include_provisional';
  deposits: Array<{ id: string; deposit_key: string; canonical_name_ru: string; canonical_name_en: string;
    commodity: string; orientation_role: string; metadata: Record<string, unknown>; latitude: number; longitude: number }>;
  collars: GeologyCollar[];
}
export interface GeologyBoreholeCensus {
  census_kind: 'archive_borehole_census';
  source_reported_campaign: {
    group_key: string; dataset_key: string; publication_state: GeologyPublicationState;
    verification: string; source: string; pdf_pages: number[]; period: string;
    holes_total: number; operational_exploration_holes: number;
    redrilled_detailed_exploration_holes: number; holes_used_in_report: number;
    holes_through_bentonite: number; holes_through_other_rocks: number;
    yearly: Array<{ year: number; holes: number }>;
  };
  map_ready_collars: {
    count: number; group_keys?: string[];
    method_breakdown?: Array<{
      group_key: string; count: number; method: string;
      practical_uncertainty_m: number | string;
      publication_state: GeologyPublicationState;
    }>;
    meaning?: string;
  };
  dataset_scoped_identifier_inventory: {
    record_count: number; confirmed_record_count: number; tentative_record_count: number;
    source_inventoried_record_count?: number; ocr_candidate_record_count?: number;
    dataset_count: number; meaning: string; limitation: string;
    coordinate_catalogue_1975?: {
      source: string; source_sha256: string; catalogue_rows: number;
      borehole_rows: number; borehole_identifiers: number;
      merged_existing_1975_identifiers: number;
      new_dataset_scoped_identifiers: number; map_ready_boreholes: number;
      unresolved_borehole_positions: number; pit_rows: number;
    };
  };
  interpretation_rules: string[];
}
export interface GeologyBoreholeRegistryInterval {
  interval_key: string; top_m: number; bottom_m: number; thickness_m: number;
  raw_lithology?: string | null; verification_status: string;
  normalized_label_ru?: string | null; normalized_label_en?: string | null;
  color?: string | null; pattern?: string | null;
  metadata?: {
    interval_role?: string; is_bentonite?: boolean; confidence?: string;
    raw_interval_row?: Record<string, unknown>;
    [key: string]: unknown;
  } | null;
}
export interface GeologyBentoniteInterval {
  from_m: number; to_m: number; thickness_m: number;
  unit_code?: string; interpretation?: string;
}
export type GeologyCasingEntry =
  | number
  | { fromM: number; toM: number; diameterMm: number };
export interface GeologyBoreholeRegistryCollar {
  id: string; raw_x?: string | null; raw_y?: string | null; raw_z?: string | null;
  projected_x?: number | string | null; projected_y?: number | string | null;
  elevation_m?: number | string | null; source_crs?: string | null;
  axis_interpretation?: string | null; coordinate_operation?: string | null;
  operation_accuracy_m?: number | string | null; practical_uncertainty_m?: number | string | null;
  verification_status: string; coordinate_role: string;
  latitude?: number | null; longitude?: number | null;
  metadata?: Record<string, unknown>;
}
export interface GeologyBoreholeRegistryRecord {
  record_key: string; master_record_id?: string | null;
  source_group: string; source_index: number;
  identifier: string; source_label: string; confidence: 'confirmed' | 'tentative';
  evidence_tier?: string | null; evidence_confidence?: string | null;
  publication_state: GeologyPublicationState; source: string; source_note?: string | null;
  source_page?: number | string | null; source_type?: string | null;
  coordinate_source?: string | null; coordinate_source_page?: number | string | null;
  coordinate_sector?: string | null;
  coordinate_status: 'candidate_global' | 'local_only' | 'not_available';
  spatial_status: GeologyBoreholeSpatialStatus;
  spatial_evidence: string;
  geometry_status:
    | 'reviewed_intervals' | 'normalized_lithology_log' | 'visual_summary_intervals'
    | 'candidate_lithology_log'
    | 'reported_depth_trace' | 'candidate_collar' | 'canonical_identifier_only'
    | 'identifier_only' | 'tentative_identifier_only';
  trajectory_method: 'deviation_survey' | 'assumed_vertical' | 'unknown';
  depth_status:
    | 'reviewed_intervals' | 'normalized_intervals' | 'ocr_candidate_intervals'
    | 'visual_summary'
    | 'reported_final_depth' | 'not_structured';
  final_depth_m?: number | null;
  bentonite_from_m?: number | null; bentonite_to_m?: number | null;
  bentonite_thickness_m?: number | null;
  bentonite_intervals?: GeologyBentoniteInterval[];
  casing?: GeologyCasingEntry[];
  drilling_run_count?: number; lithology_interval_count?: number;
  sample_count?: number; section_map_occurrence_count?: number; ocr_mention_count?: number;
  data_completeness?: string | null; source_context?: string | null;
  limitations?: string | null; qa_flags?: string[];
  canonical_borehole_id?: string; canonical_borehole_key?: string;
  dataset_key?: string; dataset_version?: string;
  canonical_publication_state?: GeologyPublicationState;
  collar?: GeologyBoreholeRegistryCollar | null;
  intervals: GeologyBoreholeRegistryInterval[];
}
export type GeologyBoreholeSpatialStatus =
  | 'candidate_global' | 'local_coordinates' | 'identifier_only'
  | 'section_only' | 'local_grid_unresolved'
  | 'borehole_log_no_collar'
  | 'section_or_plan_only' | 'projected_grid_crs_unknown'
  | 'cross_document_inferred' | 'ocr_candidate';
export interface GeologyBoreholeSourceGroupSummary {
  group_key: string; record_count: number;
  confirmed_record_count: number; tentative_record_count: number;
  coordinate_status: 'candidate_global' | 'local_only' | 'not_available';
  spatial_status: GeologyBoreholeSpatialStatus;
  spatial_evidence: string; source: string;
  publication_state: GeologyPublicationState;
}
export interface GeologyBoreholeRegistry {
  registry_kind: 'dataset_scoped_borehole_identifier_registry';
  summary: {
    indexed_records: number; confirmed_records: number; tentative_records: number;
    source_groups: number; map_ready_records: number; records_with_reviewed_intervals: number;
    records_with_intervals?: number; records_with_candidate_intervals?: number;
    local_or_section_records: number; identifier_only_records: number;
    complete_normalized_logs?: number; records_with_reported_depth?: number;
    records_with_bentonite_summary?: number; drilling_run_rows?: number;
    sample_laboratory_rows?: number; laboratory_result_values?: number;
    section_map_occurrences?: number; ocr_mentions?: number;
    source_inventory_files?: number; missing_source_items?: number;
    source_reported_campaign_holes: number;
  };
  source_group_summaries: GeologyBoreholeSourceGroupSummary[];
  records: GeologyBoreholeRegistryRecord[];
  limitations: { identity: string; campaign: string; geometry: string };
}
export interface GeologyBoreholeDrillingRun {
  index: number; run_no?: number | null; top_m?: number | null; bottom_m?: number | null;
  drilled_m_printed?: number | null; drilled_m_computed?: number | null;
  core_m_printed?: number | null; core_recovery_pct_printed?: number | null;
  core_recovery_pct_computed?: number | null; confidence?: string | null;
  raw: Record<string, unknown>;
}
export interface GeologyLaboratoryResult {
  parameter: string; raw_value: string; normalized_value?: number | null;
  unit?: string | null; method?: string | null; qualifier?: string | null;
  verification_status: string;
}
export interface GeologyBoreholeSample {
  id: string; sample_key: string; top_m: number; bottom_m: number; sample_type: string;
  metadata: {
    sample_number?: string | number; confidence?: string; source_file?: string;
    source_page?: number | string | null; raw_sample_row?: Record<string, unknown>;
    [key: string]: unknown;
  };
  laboratory_results: GeologyLaboratoryResult[];
}
export interface GeologyBoreholeOccurrence {
  occurrence_id?: string; mention_id?: string; source_year: number | string;
  borehole_id?: string; borehole_id_candidate?: string; source_file: string;
  source_page?: number | string | null; context_type?: string; context_id?: string;
  terminal_depth_m?: number | string | null; confidence?: string;
  source_context?: string; context?: string; normalization_note?: string;
}
export interface GeologyBoreholeGap {
  id: string; gap_type: string; label: string; impact: string;
  blocks_publication: boolean; status: string;
  metadata: {
    severity?: string; expected?: string; available?: string; missing?: string;
    required_resolution?: string; [key: string]: unknown;
  };
}
export interface GeologyBoreholeDetail {
  id: string; borehole_key: string; printed_identifier: string;
  lifecycle_metadata: Record<string, unknown>;
  group_key: string; version: string; publication_state: GeologyPublicationState;
  raw_x?: string | null; raw_y?: string | null; raw_z?: string | null;
  projected_x?: number | string | null; projected_y?: number | string | null;
  elevation_m?: number | string | null; source_crs?: string | null;
  coordinate_operation?: string | null; practical_uncertainty_m?: number | string | null;
  latitude?: number | null; longitude?: number | null;
  intervals: GeologyBoreholeRegistryInterval[];
  drilling_runs: GeologyBoreholeDrillingRun[];
  samples_laboratory: GeologyBoreholeSample[];
  section_map_occurrences: GeologyBoreholeOccurrence[];
  ocr_candidate_mentions: GeologyBoreholeOccurrence[];
  source_gaps: GeologyBoreholeGap[];
  source_document?: {
    id: string; canonical_filename: string; relative_path: string;
    integrity_status: string; page_number?: number | null;
  } | null;
  data_contract: Record<string, unknown>;
}
export interface GeologySceneInterval {
  borehole_key: string; top_m: number; bottom_m: number;
  raw_lithology?: string | null; verification_status: string;
  normalized_label_en?: string | null; color?: string | null;
}
export interface GeologySceneData {
  dataset_version_id: string;
  collars: Array<Pick<GeologyCollar, 'borehole_key' | 'printed_identifier' | 'projected_x' | 'projected_y' | 'elevation_m' | 'verification_status' | 'practical_uncertainty_m' | 'longitude' | 'latitude'>>;
  intervals: GeologySceneInterval[];
  vertical_exaggeration_default: number;
  disclaimer: string;
}
export type DepositConfidenceClass =
  | 'observed' | 'strongly_constrained' | 'interpolated'
  | 'weakly_constrained' | 'extrapolated' | 'insufficient_evidence';
export interface GeologyDepositCatalogueFeature {
  catalogue_index: number; record_key: string;
  entity_type: 'borehole' | 'pit'; identifiers: string[];
  printed_label: string; section: string; source_page: number;
  original: { x: string[]; y: string[]; h: string[] };
  corrected: { x?: number | null; y?: number | null; h?: number | null; note: string };
  effective: {
    northing_m?: number | null; easting_m?: number | null; elevation_m?: number | null;
  };
  coordinate_status: string; coordinate_confidence: string;
  crs: {
    source: string; candidate: string; status: string; practical_uncertainty_m: number;
  };
  candidate_wgs84?: { lat: number; lon: number } | null;
  has_local_position: boolean; qa_note: string;
  model_input: false; exclusion_reason: string;
}
export interface GeologyDepositModelBorehole {
  id: string; borehole_key: string; identifier: string; year: number;
  section?: string | null; local_collar: [number, number, number];
  projected: { northing_m: number; easting_m: number; elevation_m: number };
  total_depth_m: number; azimuth_deg?: number | null; dip_deg: number;
  trajectory_method: 'assumed_vertical' | 'deviation_survey';
  assumption_confidence: string; coordinate_confidence: string;
  interval_confidence: string;
  bentonite_intervals: Array<{
    from_m: number; to_m: number; thickness_m: number;
    top_elevation_m: number; bottom_elevation_m: number;
  }>;
  source: { file: string; page?: number | null; evidence_region_id?: string | null };
  qa: string[];
}
export interface GeologyDepositSurfaceVertex {
  grid: [number, number]; local: [number, number, number];
  top_elevation_m: number; bottom_elevation_m: number;
  ground_elevation_m: number; thickness_m: number;
  nearest_observation_m: number; confidence: DepositConfidenceClass;
  confidence_score: number;
}
export interface GeologyDepositModel {
  model_id: string; model_version: string; model_hash: string; input_hash: string;
  status: 'experimental' | 'preliminary' | 'reviewed' | 'approved';
  title: string; created_at: string;
  input_snapshot: {
    dataset_key: string; dataset_version_id: string; dataset_version: string;
    publication_state: GeologyPublicationState; included_boreholes: number;
    excluded_catalogue_records: number;
  };
  parameters: Record<string, unknown>;
  crs: {
    source: string; source_status: string; render: string; wgs84: string;
    axis_interpretation: string; operation: string;
    nominal_operation_accuracy_m: number; practical_historical_uncertainty_m: number;
  };
  origin: { easting_m: number; northing_m: number; vertical_datum_m: number };
  support: {
    boundary: Array<[number, number]>; area_m2: number;
    extrapolation_enabled: boolean; unsupported_extrapolated_volume_m3: number;
    median_nearest_borehole_spacing_m: number; geological_constraints: string[];
  };
  boreholes: GeologyDepositModelBorehole[];
  observed_contacts: Array<{
    borehole_key: string; identifier: string; contact: 'top' | 'bottom';
    local: [number, number, number]; elevation_m: number;
    source_file: string; source_page?: number | null; evidence_region_id?: string | null;
  }>;
  surface: {
    vertices: GeologyDepositSurfaceVertex[]; triangles: Array<[number, number, number]>;
    cells: Array<{
      vertices: [number, number, number, number]; area_m2: number;
      thickness_m: number; volume_m3: number; confidence: DepositConfidenceClass;
    }>;
  };
  solid: {
    positions: Array<[number, number, number]>;
    triangles: Array<[number, number, number]>;
    closed: boolean; domain_count: number; domain_note: string;
  };
  metrics: {
    volume_m3: number; volume_by_confidence_m3: Record<string, number>;
    volume_by_section: { status: string; reason: string };
    volume_by_quality: { status: string; reason: string };
    thickness_m: { minimum: number; maximum: number; average: number };
    top_elevation_m: { minimum: number; maximum: number };
    bottom_elevation_m: { minimum: number; maximum: number };
    supporting_boreholes: number;
    tonnage: { status: string; reason: string };
  };
  validation: {
    method: string; sample_count: number;
    top_surface: { mae_m: number; rmse_m: number; max_abs_error_m: number; bias_m: number };
    bottom_surface: { mae_m: number; rmse_m: number; max_abs_error_m: number; bias_m: number };
    residuals: Array<{ borehole: string; top_error_m: number; bottom_error_m: number }>;
    interpretation: string;
  };
  uncertainty: {
    method: string; toggle_supported: boolean;
    classes: Array<{ key: DepositConfidenceClass; color: string; present?: boolean }>;
  };
  availability: Record<string, { available: boolean; reason: string }>;
  disclaimers: string[];
  catalogue_features: GeologyDepositCatalogueFeature[];
  cache: { hit: boolean; artifact: string };
}
export interface GeologyDepositReadiness {
  catalogue: {
    total_records: number; borehole_records: number; test_pit_records: number;
    records_with_local_coordinates: number; boreholes_with_local_coordinates: number;
    test_pits_with_local_coordinates: number; unresolved_coordinate_records: number;
    source: string; pages: number; crs_status: string;
  };
  archive: {
    indexed_borehole_identities: number; coordinate_identities: number;
    with_total_depth: number; with_bentonite_intervals: number;
    with_reviewed_lithology: number; with_deviation_survey: number;
    using_vertical_assumption: number; unresolved_conflicts: number;
    records_requiring_review: number; open_source_gaps: number;
    accepted_cross_dataset_matches: number;
  };
  current_model: {
    dataset_key: string; model_ready_boreholes: number;
    model_ready_percentage_of_catalogue_boreholes: number; status: string;
    exclusion_summary: Array<{ reason: string; count: number; scope: string }>;
  };
  interpretation: string;
}
export interface GeologyMatchProposal {
  correlation_id?: string | null;
  source: {
    borehole_id: string; borehole_key: string; dataset_version_id: string;
    group: string; identifier: string;
  };
  target: {
    borehole_id: string; borehole_key: string; dataset_version_id: string;
    group: string; identifier: string;
  };
  proposed_confidence: number; confidence_class: 'high' | 'medium' | 'low';
  review_status: 'proposed' | 'accepted' | 'rejected' | 'in_review';
  evidence: {
    identifier_exact: boolean; projected_distance_m: number;
    collar_elevation_delta_m: number; coordinate_status: string; explanation: string;
  };
  conflict_flags: string[];
}
export interface GeologyMatchCandidates {
  items: GeologyMatchProposal[];
  summary: { proposals: number; accepted: number; rejected: number; pending: number };
  rules: string[];
}
export interface GeologyConflict {
  id: string; conflict_key: string; title: string; reason: string; severity: 'info' | 'warning' | 'critical';
  resolution_state: string; reviewer_decision?: string | null; metadata: Record<string, unknown>;
  group_key: string; version: string; publication_state: GeologyPublicationState;
  assertions?: Array<Record<string, unknown>>;
}
export interface GeologyGap {
  id: string; gap_type: string; label: string; missing_from?: number | null; missing_to?: number | null;
  impact: string; blocks_publication: boolean; status: string; metadata: Record<string, unknown>;
  group_key: string; version: string; publication_state: GeologyPublicationState;
}

export const geologyV2Api = {
  models(): Promise<GeoAgentModelCatalog> { return api.request('/geology/models'); },
  manifest(): Promise<GeologyArchiveManifest> { return api.request('/geology/archive/manifest'); },
  sourceGroups(): Promise<{ items: GeologySourceGroup[] }> { return api.request('/geology/archive/source-groups'); },
  datasets(): Promise<{ items: GeologyDataset[] }> { return api.request('/geology/datasets'); },
  sources(group?: string, page = 1, pageSize = 100): Promise<{ items: GeologySourceFile[]; total: number }> {
    const query = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
    if (group) query.set('source_group', group);
    return api.request(`/geology/sources?${query.toString()}`);
  },
  map(scope: 'published' | 'include_provisional' = 'published'): Promise<GeologyMapFeatures> {
    return api.request(`/geology/maps/features?knowledge_scope=${scope}`);
  },
  boreholeCensus(): Promise<GeologyBoreholeCensus> {
    return api.request('/geology/boreholes/census');
  },
  boreholeRegistry(): Promise<GeologyBoreholeRegistry> {
    return api.request('/geology/boreholes/registry');
  },
  boreholeDetail(boreholeKey: string): Promise<GeologyBoreholeDetail> {
    return api.request(`/geology/boreholes/${encodeURIComponent(boreholeKey)}`);
  },
  scene(datasetVersionId: string): Promise<GeologySceneData> {
    return api.request(`/geology/scenes/${datasetVersionId}`);
  },
  depositModel(): Promise<GeologyDepositModel> {
    return api.request('/geology/deposit-models/current');
  },
  depositReadiness(): Promise<GeologyDepositReadiness> {
    return api.request('/geology/deposit-models/readiness');
  },
  depositMatches(): Promise<GeologyMatchCandidates> {
    return api.request('/geology/deposit-models/matches');
  },
  reviewDepositMatch(
    proposal: GeologyMatchProposal,
    action: 'accept' | 'reject' | 'reopen',
    reason: string,
  ): Promise<Record<string, unknown>> {
    return api.request('/geology/deposit-models/matches/review', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ proposal, action, reason }),
    });
  },
  buildDepositModel(force = false): Promise<Record<string, unknown>> {
    return api.request('/geology/deposit-models/build', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        dataset_key: 'sarigyukh_gilanik_1963_v1',
        force,
      }),
    });
  },
  depositModelExportUrl(format: 'json' | 'boreholes_csv' | 'intervals_csv' | 'obj' | 'gltf' | 'geojson' | 'sections' | 'validation' | 'volume'): string {
    return `${API_BASE}/api/v1/geology/deposit-models/current/export?format=${format}`;
  },
  conflicts(scope: 'published' | 'include_provisional' = 'published'): Promise<{ items: GeologyConflict[] }> {
    return api.request(`/geology/conflicts?knowledge_scope=${scope}`);
  },
  gaps(scope: 'published' | 'include_provisional' = 'published'): Promise<{ items: GeologyGap[] }> {
    return api.request(`/geology/quality/gaps?knowledge_scope=${scope}`);
  },
  sourceContentUrl(sourceId: string): string { return `${API_BASE}/api/v1/geology/sources/${sourceId}/content`; },
  sourcePageUrl(sourceId: string, page: number): string { return `${API_BASE}/api/v1/geology/sources/${sourceId}/pages/${page}`; },
};
